// Órdenes de mantenimiento de habitaciones. Hotel → Mantenimiento.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, CheckCircle2, Plus, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, fmtFecha, useSort, SortTh, hotelRpc, hotelTabla, QK, Habitacion, useDialogoEnLinea } from './shared';

type EstadoFiltro = 'abierta' | 'cerrada' | 'todas';
type TipoOrden = 'fuera_orden' | 'fuera_servicio' | 'preventivo';
const TIPO_LBL: Record<TipoOrden, string> = { fuera_orden: 'Fuera de orden', fuera_servicio: 'Fuera de servicio', preventivo: 'Preventivo' };

interface OrdenMant {
  id: string; habitacion: string; tipo: TipoOrden; descripcion: string;
  estado: 'abierta' | 'cerrada'; desde: string; hasta: string | null; created_at: string; cerrada_at: string | null;
  origen?: string; categoria?: string | null; camarera?: string | null; foto_id?: string | null; // 949: «Algo roto» de la camarera
}

export function MantenimientoHotel() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<EstadoFiltro>('abierta');
  const { data: ordenes = [], isLoading } = useQuery<OrdenMant[]>({
    queryKey: [QK, 'mantenimiento', filtro], queryFn: () => hotelRpc<OrdenMant[]>('mantenimiento_listar', { _estado: filtro === 'todas' ? null : filtro }),
  });
  const { data: habs = [] } = useQuery<Habitacion[]>({
    queryKey: [QK, 'habitaciones'], queryFn: () => hotelTabla<Habitacion>('habitaciones', 'order=orden,numero&es_activo=eq.true'),
  });

  const sort = useSort<OrdenMant>(ordenes, (r, k) => {
    switch (k) {
      case 'habitacion': return r.habitacion;
      case 'tipo': return r.tipo;
      case 'estado': return r.estado;
      case 'desde': return r.desde;
      case 'hasta': return r.hasta;
      case 'created_at': return r.created_at;
      default: return null;
    }
  });

  const [nueva, setNueva] = useState(false);
  const [verFoto, setVerFoto] = useState<OrdenMant | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: [QK] });

  const cerrar = useMutation({
    mutationFn: (id: string) => hotelRpc('mantenimiento_cerrar', { _id: id }),
    onSuccess: () => { toast.success('Orden cerrada'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (verFoto) return <FotoAveria orden={verFoto} onClose={() => setVerFoto(null)} />;

  // La nueva orden es una página (no ventana modal): reemplaza a la lista y vuelve con «Volver».
  if (nueva) {
    return <NuevaOrden habitaciones={habs} onClose={() => setNueva(false)} onCreada={() => { setNueva(false); refrescar(); }} />;
  }

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Wrench className="w-5 h-5" /> Mantenimiento</h1>
        <Button size="sm" onClick={() => setNueva(true)}><Plus className="w-4 h-4 mr-1" /> Nueva orden</Button>
      </div>

      <div className="flex gap-2">
        {(['abierta', 'cerrada', 'todas'] as EstadoFiltro[]).map((f) => (
          <button key={f} type="button" onClick={() => setFiltro(f)}
            className={`rounded px-3 py-1 text-sm capitalize ${filtro === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            {f === 'todas' ? 'Todas' : f === 'abierta' ? 'Abiertas' : 'Cerradas'}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Órdenes ({ordenes.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">N°</th>
                <SortTh col="habitacion" sort={sort}>Habitación</SortTh>
                <SortTh col="tipo" sort={sort}>Tipo</SortTh>
                <th className="py-2 pr-3">Descripción</th>
                <SortTh col="desde" sort={sort}>Desde</SortTh>
                <SortTh col="hasta" sort={sort}>Hasta</SortTh>
                <SortTh col="estado" sort={sort}>Estado</SortTh>
                <SortTh col="created_at" sort={sort}>Creada</SortTh>
                <th className="py-2"></th>
              </tr></thead>
              <tbody>
                {sort.sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin órdenes de mantenimiento</td></tr>}
                {sort.sorted.map((o, i) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono">{o.habitacion}</td>
                    <td className="py-2 pr-3 text-xs">{TIPO_LBL[o.tipo] ?? o.tipo}</td>
                    <td className="py-2 pr-3 max-w-xs" title={o.descripcion}>
                      <div className="truncate">{o.descripcion}</div>
                      {o.origen === 'camarera' && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="rounded bg-violet-100 px-1.5 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">Camarera{o.camarera ? `: ${o.camarera}` : ''}</span>
                          {o.foto_id && <button type="button" className="inline-flex items-center gap-0.5 underline" onClick={() => setVerFoto(o)}><Camera className="h-3 w-3" /> Ver foto</button>}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">{fmtFecha(o.desde)}</td>
                    <td className="py-2 pr-3">{fmtFecha(o.hasta)}</td>
                    <td className="py-2 pr-3">
                      {o.estado === 'abierta'
                        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Abierta</span>
                        : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Cerrada</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{fmtFecha(o.created_at)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {o.estado === 'abierta' && (
                        <Button size="sm" variant="outline" isLoading={cerrar.isPending}
                          onClick={async () => { if (await dlg.confirmar(`¿Cerrar la orden de la ${o.habitacion}?`, { detalle: 'La habitación vuelve a la venta y queda sucia.', aceptar: 'Cerrar orden' })) cerrar.mutate(o.id); }}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Cerrar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NuevaOrden({ habitaciones, onClose, onCreada }: { habitaciones: Habitacion[]; onClose: () => void; onCreada: () => void }) {
  const [habitacionId, setHabitacionId] = useState('');
  const [tipo, setTipo] = useState<TipoOrden>('fuera_orden');
  const [descripcion, setDescripcion] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const crear = useMutation({
    mutationFn: () => hotelRpc('mantenimiento_abrir', {
      _d: { habitacion_id: habitacionId, tipo, descripcion: descripcion.trim(), desde: desde || null, hasta: hasta || null },
    }),
    onSuccess: () => { toast.success('Orden de mantenimiento abierta'); onCreada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={onClose}><ArrowLeft className="w-4 h-4 mr-1" /> Mantenimiento</Button>
        <h1 className="text-xl font-semibold flex items-center gap-2"><Wrench className="w-5 h-5" /> Nueva orden de mantenimiento</h1>
      </div>
      <Card className="max-w-2xl">
        <CardContent className="space-y-3 pt-6 text-sm">
        <Campo label="Habitación *">
          <select className={fieldCls} value={habitacionId} onChange={(e) => setHabitacionId(e.target.value)}>
            <option value="">— Elegir —</option>
            {habitaciones.map((h) => <option key={h.id} value={h.id}>{h.numero}{h.piso ? ` · ${/^\d/.test(h.piso) ? `Piso ${h.piso}` : h.piso}` : ''}</option>)}
          </select>
        </Campo>
        <Campo label="Tipo">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as TipoOrden)}>
            <option value="fuera_orden">Fuera de orden (bloquea la habitación)</option>
            <option value="fuera_servicio">Fuera de servicio (bloquea la habitación)</option>
            <option value="preventivo">Preventivo (no bloquea)</option>
          </select>
        </Campo>
        <Campo label="Descripción del problema *">
          <textarea className={`${fieldCls} h-20`} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Ej. fuga en el baño, aire acondicionado no enfría…" />
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Desde (opcional)"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta (opcional)"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        </div>
        <p className="text-xs text-muted-foreground">Para "fuera de orden/servicio", si la habitación tiene reservas asignadas en el rango, reasígnelas primero: el sistema rechaza la orden.</p>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={crear.isPending} disabled={!habitacionId || !descripcion.trim()} onClick={() => crear.mutate()}>Abrir orden</Button>
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Foto de la avería que tomó la camarera (949, hotel.hk_foto). Página, no modal.
function FotoAveria({ orden, onClose }: { orden: OrdenMant; onClose: () => void }) {
  const { data, isLoading } = useQuery<{ mime: string; base64: string } | null>({
    queryKey: [QK, 'hk-foto', orden.foto_id], queryFn: () => hotelRpc('hk_foto', { _id: orden.foto_id }), enabled: !!orden.foto_id,
  });
  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={onClose}><ArrowLeft className="w-4 h-4 mr-1" /> Mantenimiento</Button>
        <h1 className="text-xl font-semibold">Habitación {orden.habitacion} · foto de la avería</h1>
      </div>
      <p className="text-sm text-muted-foreground">{orden.descripcion}</p>
      {isLoading ? <div className="text-sm text-muted-foreground">Cargando…</div>
        : data ? <img src={`data:${data.mime};base64,${data.base64}`} alt="Foto de la avería" className="max-h-[70vh] max-w-full rounded-lg border" />
        : <div className="text-sm text-muted-foreground">Sin foto.</div>}
    </div>
  );
}
